// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import FilterAutocomplete from "components/FilterAutocomplete";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/MUI";

// Data
import axios from "axios";
import { withAuthContext } from "context/Auth";
import { useEffect, useState, useMemo, useCallback } from "react";
import MDBadge from "components/MDBadge";
import CarIcon from "assets/images/bg-profile.jpeg";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@mui/material";
import { withPropertyContext } from "context/Property";

function Tables({ Token, CheckToken, AdminRole }) {
	const navigate = useNavigate();
	const [data, setData] = useState([]);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [filters, setFilters] = useState({ color: "", slotNo: "", number: "", property: "", user: "" });
	const [filterOptions, setFilterOptions] = useState({ colors: [], slotNos: [], numbers: [], properties: [], users: [] });
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");

	const colorOptions = useMemo(() => (filterOptions?.colors || []).map((color) => ({ label: color, value: color })), [filterOptions?.colors]);
	const slotOptions = useMemo(
		() => (filterOptions?.slotNos || []).map((slot) => ({ label: slot, value: slot })),
		[filterOptions?.slotNos]
	);
	const numberOptions = useMemo(
		() => (filterOptions?.numbers || []).map((number) => ({ label: number, value: number })),
		[filterOptions?.numbers]
	);
	const propertyOptions = useMemo(
		() =>
			(filterOptions?.properties || []).map((property) => ({
				label: `${property?.Tower || ""} ${property?.FlatNo || ""}`.trim() || property?._id,
				value: property?._id
			})),
		[filterOptions?.properties]
	);
	const userOptions = useMemo(
		() =>
			(filterOptions?.users || []).map((user) => ({
				label: user?.name || user?.email || user?._id,
				value: user?._id
			})),
		[filterOptions?.users]
	);

	// ✅ Memoized columns
	const columns = useMemo(
		() => [
			{
				headerName: "User",
				field: "User",
				valueGetter: (params) => `${params.row?.User?.name || "-"}`
			},
			{
				headerName: "Property",
				field: "Property",
				valueGetter: (params) => `${params.row?.Property?.FlatNo || "-"}`
			},
			{
				headerName: "Tower",
				field: "Tower",
				valueGetter: (params) => `${params.row?.Property?.Tower || "-"}`
			},
			{ headerName: "Number", field: "Number" },
			{ headerName: "Color", field: "Color" },
			{ headerName: "SlotNo", field: "SlotNo" },
			{ headerName: "LicenseNo", field: "LicenseNo" },
			{ headerName: "Name", field: "Name" },
			{ headerName: "Description", field: "Description" }
		],
		[] // no dependencies, static structure
	);

	const GetCars = useCallback(() => {
		if (Token || localStorage.getItem("token")) {
			setIsLoading(true);
			const params = {
				page: currentPage,
				limit: itemsPerPage
			};
			if (filters.color) params.color = filters.color;
			if (filters.slotNo) params.slotNo = filters.slotNo;
			if (filters.number) params.number = filters.number;
			if (filters.property) params.property = filters.property;
			if (filters.user) params.user = filters.user;
			axios
				.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetAllCars`, {
					params,
					headers: {
						Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
					}
				})
				.then((res) => {
					if (res?.data?.status == 200) {
						setData(res?.data?.data);
						if (res?.data?.pagination) {
							setPagination(res?.data?.pagination);
						}
					} else {
						setError(res?.data?.message);
					}
				})
				.catch((err) => {
					setError(err?.message);
				})
				.finally(() => {
					setIsLoading(false);
				});
		} else {
			setTimeout(() => {
				CheckToken();
				GetCars();
			}, 500);
		}
	}, [Token, CheckToken, currentPage, itemsPerPage, filters]);

	const handleFilterChange = useCallback((field, value) => {
		setCurrentPage(1);
		setFilters((prev) => ({
			...prev,
			[field]: value || ""
		}));
	}, []);

	const handleClearFilters = useCallback(() => {
		setCurrentPage(1);
		setFilters({ color: "", slotNo: "", number: "", property: "", user: "" });
	}, []);

	const fetchCarFilters = useCallback(() => {
		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");
		if (!tokenHeader) return;
		setIsFilterLoading(true);
		setFilterError("");
		axios
			.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetCarFilters`, {
				headers: { Authorization: `Bearer ${tokenHeader}` }
			})
			.then((res) => {
				if (res?.data?.status === 200) {
					setFilterOptions(res?.data?.data || {});
				} else {
					setFilterError(res?.data?.message || "Unable to load filters");
				}
			})
			.catch((err) => {
				setFilterError(err?.response?.data?.message || err?.message || "Unable to load filters");
			})
			.finally(() => setIsFilterLoading(false));
	}, [Token]);

	useEffect(() => {
		fetchCarFilters();
	}, [fetchCarFilters]);

	useEffect(() => {
		GetCars();
	}, [GetCars, AdminRole]);

	const handlePageChange = (newPage, newPageSize) => {
		if (newPageSize !== itemsPerPage) {
			setItemsPerPage(newPageSize);
			setCurrentPage(1);
		} else {
			setCurrentPage(newPage);
		}
	};

	// ✅ Memoized table body
	const TableComponent = useMemo(
		() => (
			<>
				<MDBox px={2} py={2} mt={2} bgcolor="rgba(255,255,255,0.8)" borderRadius="md">
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6} md={3}>
							<FilterAutocomplete
								name="color"
								label="Color"
								value={filters.color}
								options={colorOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Type color"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<FilterAutocomplete
								name="slotNo"
								label="Slot No"
								value={filters.slotNo}
								options={slotOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Type slot"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<FilterAutocomplete
								name="number"
								label="Car Number"
								value={filters.number}
								options={numberOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Type number"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<FilterAutocomplete
								name="property"
								label="Property"
								value={filters.property}
								options={propertyOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Search property"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<FilterAutocomplete
								name="user"
								label="User"
								value={filters.user}
								options={userOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Search user"
							/>
						</Grid>
						<Grid item xs={12} display="flex" justifyContent="flex-end">
							<MDButton
								variant="outlined"
								color="info"
								size="small"
								disabled={isFilterLoading}
								onClick={handleClearFilters}
							>
								Clear filters
							</MDButton>
						</Grid>
					</Grid>
					{filterError && (
						<MDTypography variant="caption" color="error">
							{filterError}
						</MDTypography>
					)}
				</MDBox>
				<MDBox pt={3}>
					{isLoading ? (
						<MDBox lineHeight={1} textAlign="center" py={3}>
							<MDTypography display="block" variant="caption" color="text" fontWeight="medium" fontSize="16px">
								Loading...
							</MDTypography>
						</MDBox>
					) : data.length > 0 ? (
						<DataTable 
							rows={data} 
							columns={columns} 
							paginationMode="server"
							serverPagination={{
								total: pagination?.total || 0,
								page: currentPage,
								limit: itemsPerPage,
								onPageChange: handlePageChange
							}}
						/>
					) : (
						<MDBox lineHeight={1} textAlign="center">
							<MDTypography
								display="block"
								variant="caption"
								color="text"
								fontWeight="medium"
								fontSize="20px"
							>
								{error ? error : " No Data Found"}
							</MDTypography>
						</MDBox>
					)}
				</MDBox>
			</>
		),
		[data, columns, error, isLoading, pagination, currentPage, itemsPerPage, handlePageChange, filters, filterOptions, isFilterLoading, filterError, handleFilterChange, handleClearFilters]
	);

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox pt={6} pb={3}>
				<Grid container spacing={6}>
					<Grid item xs={12}>
						<Card>
							<MDBox
								mx={2}
								mt={-3}
								py={3}
								px={2}
								variant="gradient"
								bgColor="info"
								borderRadius="lg"
								coloredShadow="info"
							>
								<Grid container style={{ width: "100%" }}>
									<Grid item xs={6}>
										<MDTypography variant="h6" color="white">
											Car
										</MDTypography>
									</Grid>
									{(AdminRole == "admin" ||
										AdminRole == "superadmin" ||
										AdminRole == "manager") && (
											<Grid
												item
												xs={6}
												style={{
													display: "flex",
													alignItems: "flex-end",
													alignContent: "flex-end",
													justifyContent: "flex-end"
												}}
											>
												<MDBadge
													badgeContent="New"
													color="dark"
													variant="gradient"
													size="sm"
													onClick={() => navigate(`/CarProperty/New`)}
												/>
											</Grid>
										)}
								</Grid>
							</MDBox>

							{TableComponent}
						</Card>
					</Grid>
				</Grid>
			</MDBox>
		</DashboardLayout>
	);
}

export default withAuthContext(withPropertyContext(Tables));
